var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/constants.ts
var IMAGE_FORMATS = [
  "image/gif",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/svg+xml"
];

// src/hocs/imageFillableNode.tsx
import { OverlayPortal } from "@ballpark/design-core";
import { ItemUtils } from "@ballpark/design-creator";
import { useTransactionCallback as useTransactionCallback2 } from "@ballpark/design-plugin-react";
import { Highlight } from "@ballpark/design-plugin-selection";
import { useComponents } from "@ballpark/design-renderer-base";
import { observer } from "mobx-react";
import React2, { useState as useState2 } from "react";

// src/hooks.ts
import { FillUtils } from "@ballpark/design-creator";
import {
  useDesign as useDesign2,
  useTransactionCallback
} from "@ballpark/design-plugin-react";

// src/react.tsx
import { useDesign } from "@ballpark/design-plugin-react";
import { runInAction } from "mobx";
import {
  createContext,
  useContext,
  useEffect,
  useState
} from "react";
import { useMemoOne } from "use-memo-one";

// src/PluginImage.ts
import { makeAutoObservable } from "mobx";

// src/utils.ts
function urlToImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.src = url;
  });
}
function blobToImage(blob) {
  return __async(this, null, function* () {
    const url = URL.createObjectURL(blob);
    const image = yield urlToImage(url);
    URL.revokeObjectURL(url);
    return image;
  });
}
function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (_e) => resolve(reader.result);
    reader.onerror = (_e) => reject(reader.error);
    reader.onabort = (_e) => reject(new Error("blobToDataURL failed"));
    reader.readAsDataURL(blob);
  });
}
function resizeImageBlob(_0) {
  return __async(this, arguments, function* (imageBlob, {
    maxWidth = Infinity,
    maxHeight = Infinity
  } = {}) {
    const image = yield blobToImage(imageBlob);
    const { width, height } = image;
    let targetWidth = width;
    let targetHeight = height;
    if (width > height) {
      if (width > maxWidth) {
        targetWidth = maxWidth;
        targetHeight *= maxWidth / width;
      }
    } else if (height > maxHeight) {
      targetWidth *= maxHeight / height;
      targetHeight = maxHeight;
    }
    if (height === targetHeight && width === targetWidth)
      return { size: [targetWidth, targetHeight], imageBlob };
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");
    ctx == null ? void 0 : ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve({ size: [targetWidth, targetHeight], imageBlob: blob });
        } else {
          reject(
            new Error("resizeImage failed, could not convert canvas to blob")
          );
        }
      });
    });
  });
}
function isImageDropEvent(event) {
  var _a, _b;
  const type = (_b = (_a = event.dataTransfer) == null ? void 0 : _a.types) == null ? void 0 : _b[0];
  if (!type)
    return false;
  if (IMAGE_FORMATS.indexOf(type))
    return true;
  return false;
}
function getImageFromDropEvent(event) {
  var _a, _b;
  const blob = (_b = (_a = event.dataTransfer) == null ? void 0 : _a.files) == null ? void 0 : _b[0];
  if (!blob)
    return null;
  if (IMAGE_FORMATS.indexOf(blob.type))
    return blob;
  return null;
}

// src/actions.ts
function uploadImageBlob(imageBlob) {
  return __async(this, null, function* () {
    const { size, imageBlob: sizedImageBlob } = yield resizeImageBlob(imageBlob, {
      maxWidth: this.maxWidth,
      maxHeight: this.maxHeight
    });
    const { uploaded, url } = yield this._uploadImageBlob(
      sizedImageBlob,
      (assetUrl) => {
        this._design.transact(
          () => {
            this._design.setAssetUploaded(assetUrl);
            this._aliasMap.delete(assetUrl);
          },
          "auto"
          // prevents this change from being considered by the UndoManager if one exists
        );
      }
    );
    if (!uploaded) {
      this._design.transact(() => {
        this._aliasMap.set(url, URL.createObjectURL(sizedImageBlob));
        this._design.setAssetUploading(url);
      }, "auto");
    }
    return { url, size };
  });
}

// src/selectors.ts
function getActualUrl(url) {
  const aliasedUrl = this._aliasMap.get(url);
  if (this._design.isUploading(url) && !aliasedUrl)
    return null;
  return aliasedUrl != null ? aliasedUrl : url;
}

// src/PluginImage.ts
var defaultUploadImageBlob = (imageBlob, onUploaded) => __async(void 0, null, function* () {
  const dataURL = yield blobToDataURL(imageBlob);
  onUploaded == null ? void 0 : onUploaded(dataURL);
  return { uploaded: true, url: dataURL };
});
var PluginImage = class {
  constructor({
    design,
    maxWidth = Infinity,
    maxHeight = Infinity,
    uploadImageBlob: uploadImageBlob2 = defaultUploadImageBlob
  }) {
    this.getActualUrl = getActualUrl.bind(this);
    this.uploadImageBlob = uploadImageBlob.bind(this);
    this._aliasMap = /* @__PURE__ */ new Map();
    this._design = design;
    this.maxWidth = maxWidth;
    this.maxHeight = maxHeight;
    this._uploadImageBlob = uploadImageBlob2;
    makeAutoObservable(this, {
      _design: false
    });
  }
  set design(design) {
    this._design = design;
  }
};

// src/react.tsx
import { Fragment, jsx } from "react/jsx-runtime";
var PluginImageContext = createContext(null);
function useImage() {
  const state = useContext(PluginImageContext);
  if (state === null)
    throw new Error("useImage() called outside of ImageProvider");
  return state;
}
function useWeakImage() {
  const maybePluginImage = useContext(PluginImageContext);
  const design = useDesign();
  const pluginImage = useMemoOne(
    () => maybePluginImage != null ? maybePluginImage : new PluginImage({ design }),
    [maybePluginImage, design]
  );
  return pluginImage;
}
function ImageProvider({
  children,
  options
}) {
  const design = useDesign();
  const [pluginImage] = useState(() => new PluginImage(__spreadValues({ design }, options)));
  useEffect(() => {
    runInAction(() => {
      if (design)
        pluginImage.design = design;
    });
  }, [design, pluginImage]);
  return /* @__PURE__ */ jsx(PluginImageContext.Provider, { value: pluginImage, children });
}
function WeakImageProvider({
  children,
  options
}) {
  const state = useContext(PluginImageContext);
  if (state === null) {
    return /* @__PURE__ */ jsx(ImageProvider, { options, children });
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}

// src/hooks.ts
function useInsertImageBlobAsFill() {
  const design = useDesign2();
  const image = useWeakImage();
  return useTransactionCallback(
    (blob, ids) => __async(this, null, function* () {
      const { size, url } = yield image.uploadImageBlob(blob);
      design.transact(() => {
        ids.forEach((id) => {
          const fill = FillUtils.createImageFill(url, size);
          design.addFill({ id, fill });
        });
      });
    }),
    [design, image]
  );
}

// src/hocs/imageFillableNode.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function imageFillableNode(Node, BaseNode = Node) {
  const ImageFillableNode = observer(
    React2.forwardRef(function ImageFillableNode2(props, ref) {
      const { Interaction } = useComponents();
      const insertImageBlobAsFill = useInsertImageBlobAsFill();
      const [isHighlighted, setIsHighlighted] = useState2(false);
      const { data } = props;
      const onDragOver = useTransactionCallback2((event) => {
        if (!isImageDropEvent(event))
          return;
        setIsHighlighted(true);
        event.preventDefault();
        event.stopPropagation();
      }, []);
      const onDragEnter = useTransactionCallback2((event) => {
        if (!isImageDropEvent(event))
          return;
        event.preventDefault();
        event.stopPropagation();
        setIsHighlighted(true);
      }, []);
      const onDragLeave = useTransactionCallback2((event) => {
        if (!isImageDropEvent(event))
          return;
        setIsHighlighted(false);
      }, []);
      const onDrop = useTransactionCallback2(
        (event) => {
          const blob = getImageFromDropEvent(event);
          if (!blob)
            return;
          event.preventDefault();
          event.stopPropagation();
          setIsHighlighted(false);
          insertImageBlobAsFill(blob, [data.id]);
        },
        [data.id, insertImageBlobAsFill]
      );
      if (!ItemUtils.isFillable(data))
        return /* @__PURE__ */ jsx2(Node, __spreadProps(__spreadValues({}, props), { ref }));
      return /* @__PURE__ */ jsxs(
        Interaction,
        {
          onDragEnter,
          onDragOver,
          onDragLeave,
          onDragEnd: onDragLeave,
          onDrop,
          children: [
            /* @__PURE__ */ jsx2(Node, __spreadProps(__spreadValues({}, props), { ref })),
            isHighlighted && /* @__PURE__ */ jsx2(OverlayPortal, { children: /* @__PURE__ */ jsx2(Highlight, __spreadValues({ BaseNode }, props)) })
          ]
        }
      );
    })
  );
  return ImageFillableNode;
}
export {
  IMAGE_FORMATS,
  ImageProvider,
  PluginImage,
  WeakImageProvider,
  blobToDataURL,
  blobToImage,
  getImageFromDropEvent,
  imageFillableNode,
  isImageDropEvent,
  resizeImageBlob,
  urlToImage,
  useImage,
  useInsertImageBlobAsFill,
  useWeakImage
};
